import React, {useMemo} from 'react';
import ReactDOM from 'react-dom';

import {assert} from 'Asserts';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {DocumentNoteProvider} from 'notebook/documents/DocumentNoteProvider';
import {TableCellEditor, TableCellEditorProps} from 'notebook/tables/core-ui/TableCellEditor';
import useMultiWindowEditing from 'notebook/tables/core-ui/useMultiWindowEditing';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  HydratedTableContext,
  HydratedTableProvider,
} from 'notebook/tables/store/contexts/HydratedTableContext';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {TableModelProvider} from 'notebook/tables/store/contexts/TableModelContext';
import {useCrossStepFormulaBox} from 'notebook/tables/store/hooks/useCrossStepFormulaBox';
import {TableIndex, Cell} from 'notebook/tables/TableConstants';

import {TableNoteContext} from '../store/contexts/TableNoteContext';
import {getEvaluatedCell, getEvaluatedTable} from '../store/selectors/TableSelectors';
import {TableEditableProps} from './TableEditableProps';

interface CrossStepFormulaBoxProps {
  stepName: string;
  stepIdBeingEdited: string;
  stepIdBeingDisplayed: string;
  tableName: string;
  index: TableIndex;
  tableNamespace: string;
  onReturnToStep: () => void;
}

function CrossStepFormulaBoxInner({
  stepName,
  stepIdBeingEdited,
  stepIdBeingDisplayed,
  tableName,
  index,
  tableNamespace,
  onReturnToStep,
}: CrossStepFormulaBoxProps): JSX.Element {
  const {cid, cellError, lastIndex, updateCell} = useCrossStepFormulaBox(index);
  const formulaBoxRef = React.useRef<HTMLDivElement>(null);

  useMultiWindowEditing({
    tableNamespace,
    fileId: stepIdBeingEdited,
    tableName,
    documentName: stepName,
    domNode: ReactDOM.findDOMNode(formulaBoxRef.current) as Element,
    multiStartIndex: null,
    multiEndIndex: null,
    hasFormulasDisabled: false,
    multiActive: false,
    multiEditing: true,
    multiCrossEditing: true,
    onEditFinalize: () => {
      const {formula, typedValue} = SpreadsheetStore.getState(tableNamespace, tableName);
      updateCell({text: formula, typedValue});
      finishEditing();
    },
  });

  function finishEditing(): void {
    SpreadsheetEvalUtil.setMultiEditingForTable(
      {tableName, documentName: stepName},
      tableNamespace,
      stepIdBeingEdited,
      {
        index: null,
        editing: false,
        text: '',
        typedValue: null,
        selection: null,
      }
    );
  }

  function onCellEnter(e: {text: string; cellData: Partial<Cell>}): void {
    updateCell({...e.cellData, text: e.text});
    finishEditing();
    onReturnToStep();
  }

  const {getNote} = TableNoteContext.useContext();
  const {selectTable} = HydratedTableContext.useContext();

  const note = getNote();
  const table = selectTable(note);
  assert(table != null, `Table not found for ${tableName}`);

  const cell = table.table[index[0]][index[1]];
  const computedTable = getEvaluatedTable(tableName)(note) ?? [];
  const evaluatedCell = getEvaluatedCell(tableName, index[0], index[1])(note) ?? undefined;
  const columnConstraint = table.columnConstraints?.[index[1]] ?? null;
  const rowData = table.rowData?.[index[0]] ?? null;

  const tableEditableProps: TableEditableProps = {
    table,
    computedTable,
    staleRows: [],
    errorRows: [],
    warningRows: [],
    fileId: stepIdBeingEdited,
    tableNamespace,
    cid,
    index: 0,
    searchResultsTree: null,
    selectedSearchResultTree: null,
    multiEditing: true,
    setMultiEditingForTable: () => {},
    canInsert: () => false,
    tryInsert: () => false,
    insertItemHandler: () => {},
    tabHandler: () => {},
    setSelectionAbove: () => {},
    setSelectionBelow: () => {},
    updateHandler: () => {},
    canAttachFileToCell: () => false,
    canAddRemoveColumns: false,
    canAddColumnsFreeform: false,
    canRename: false,
    isTableNameUsed: () => false,
    locked: false,
    getEditWhileCellLockedWarning: () => '',
    editWhileTableLockedWarning: null,
    disableFormulas: false,
    showExcessLabels: false,
    showTableRowExcessLabels: false,
    showFormulaBox: false,
    viewer: false,
    dirty: false,
    isMaximized: false,
    isSplitScreen: false,
    isFocused: false,
    disableMaximizedTables: false,
    setMaximizedTable: () => {},
    indentation: 0,
    isCollapsible: false,
    isCompactBottomSpacing: false,
    isLeftAlignColumnHeader: false,
    isLeftAlignRowHeader: false,
    hasPlaceholderRows: false,
    disableLegacyHeader: false,
    forceCollapsed: false,
    isNestedInParentComponent: false,
    isTableHeaderFaded: false,
    storeState: () => {},
    setClipboard: () => {},
    selectorsEnabled: false,
    selectorsLink: {value: [], selectorUsageCounts: {}},
    openManageSelectorsModal: () => {},
  } satisfies TableEditableProps;

  const tableCellEditorProps: TableCellEditorProps = {
    i: index[0],
    j: index[1],
    fileId: stepIdBeingEdited,
    tableName,
    tableNamespace,
    cid,
    getLastIndex: () => lastIndex ?? index,
    disableFormulas: false,
    cellEnterHandler: onCellEnter,
    cellEscapeHandler: () => {
      finishEditing();
    },
    cell,
    evaluatedCell,
    columnConstraint,
    rowData,
    storeState: () => {},
    commitCell: (index: TableIndex, text: string) => {
      updateCell({text});
    },
    moveFromCell: () => {},
    keyDownHook: () => {},
    cellError,
    stepIdBeingDisplayed,
  };

  const documentEditorContextValue = useMemo(() => ({documentName: stepName}), [stepName]);

  return (
    <>
      <DocumentEditorContext.Provider value={documentEditorContextValue}>
        <TableModelProvider tableProps={tableEditableProps}>
          <RowModelProvider rowIndex={index[0]}>
            <TableCellEditor
              {...tableCellEditorProps}
              floating={{stepName, navigateToEditingCell: onReturnToStep}}
            />
          </RowModelProvider>
        </TableModelProvider>
      </DocumentEditorContext.Provider>
    </>
  );
}

export function CrossStepFormulaBox(props: CrossStepFormulaBoxProps): JSX.Element {
  return (
    <DocumentNoteProvider documentId={props.stepIdBeingEdited} tableNamespace={props.tableNamespace}>
      <HydratedTableProvider tableName={props.tableName}>
        <CrossStepFormulaBoxInner {...props} />
      </HydratedTableProvider>
    </DocumentNoteProvider>
  );
}
